import { useState, useEffect, ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * Terminal Demo - Interactive installation and configuration showcase
 *
 * Tabs:
 * - Install: Helm installation commands
 * - Configure: InferencePool CRD example
 * - Monitor: kubectl output showing status
 */

type Tab = 'install' | 'configure' | 'monitor';

interface TabContentProps {
    activeTab: Tab;
}

interface TerminalLine {
    text: string;
    prompt?: boolean;
    header?: boolean;
}

const installLines: TerminalLine[] = [
    { prompt: true, text: 'helm install forecaster oci://registry.forecaster.ai/helm/forecaster \\' },
    { text: '    --namespace forecaster-system --create-namespace' },
    { text: '' },
    { text: 'NAME: forecaster' },
    { text: 'NAMESPACE: forecaster-system' },
    { text: 'STATUS: deployed' },
];

const configureContent = `apiVersion: forecaster.dev/v1
kind: InferencePool
metadata:
  name: production
  annotations:
    forecaster.io/compliance-zones: "EU"
    forecaster.io/region-preferences: "eu-west-1, eu-central-1"
spec:
  providers:
    - name: aws
      regions: [us-east-1, us-west-2]
    - name: gcp
      regions: [us-central1]
    - name: azure
      regions: [eastus]
  slo:
    latencyP99Ms: 100
    availabilityTarget: 0.999
  optimization:
    costOptimize: true
    spotEnabled: true
    spotMaxPercentage: 40`;

const monitorLines: TerminalLine[] = [
    { prompt: true, text: 'kubectl get inferencepool' },
    { header: true, text: 'NAME         PROVIDERS   COST/TOKEN   STATUS   AGE' },
    { text: 'production   3           $0.0012      Healthy  2d' },
    { text: 'staging      2           $0.0018      Healthy  5d' },
    { prompt: true, text: 'kubectl get pods -l app=forecaster' },
    { header: true, text: 'NAME                          READY   STATUS    RESTARTS   AGE' },
    { text: 'forecaster-controller-7f8d9   1/1     Running   0          2d' },
    { text: 'forecaster-predictor-4k2m1    1/1     Running   0          2d' },
    { text: 'forecaster-router-9x3n2       1/1     Running   0          2d' },
];

const TabContent = ({ activeTab }: TabContentProps) => {
    const [displayedLines, setDisplayedLines] = useState<number>(0);
    const [copied, setCopied] = useState(false);

    // Reset animation when tab changes
    useEffect(() => {
        setDisplayedLines(0);
        const lines = activeTab === 'install' ? installLines : activeTab === 'monitor' ? monitorLines : [];

        if (activeTab === 'configure') {
            setDisplayedLines(1); // Show all for configure
            return;
        }

        const timer = setInterval(() => {
            setDisplayedLines((prev) => {
                if (prev >= lines.length) {
                    clearInterval(timer);
                    return prev;
                }
                return prev + 1;
            });
        }, 300);

        return () => clearInterval(timer);
    }, [activeTab]);

    const handleCopy = () => {
        let content = '';
        if (activeTab === 'install') {
            content = installLines.filter(l => l.prompt).map(l => l.text).join('\n');
        } else if (activeTab === 'configure') {
            content = configureContent;
        } else {
            content = monitorLines.filter(l => l.prompt).map(l => l.text).join('\n');
        }
        navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (activeTab === 'configure') {
        return (
            <div className="relative">
                <button
                    onClick={handleCopy}
                    className="absolute top-3 right-3 p-2 hover:bg-white/10 rounded transition-colors z-10"
                    title="Copy to clipboard"
                >
                    {copied ? (
                        <Check className="w-4 h-4 text-green-500" />
                    ) : (
                        <Copy className="w-4 h-4 text-gray-500" />
                    )}
                </button>
                <pre className="text-sm leading-relaxed overflow-x-auto">
                    <code>
                        {configureContent.split('\n').map((line, i) => {
                            // Syntax highlighting
                            let highlighted: ReactNode = line;
                            if (line.startsWith('apiVersion:') || line.startsWith('kind:') || line.startsWith('metadata:') || line.startsWith('spec:')) {
                                highlighted = <span className="text-volt-400">{line}</span>;
                            } else if (line.includes(':') && !line.trim().startsWith('-')) {
                                const [key, ...rest] = line.split(':');
                                highlighted = (
                                    <>
                                        <span className="text-gray-500">{key}</span>
                                        <span className="text-gray-400">:</span>
                                        <span className="text-gray-300">{rest.join(':')}</span>
                                    </>
                                );
                            } else if (line.trim().startsWith('-')) {
                                highlighted = <span className="text-gray-300">{line}</span>;
                            } else {
                                highlighted = <span className="text-gray-400">{line}</span>;
                            }
                            return (
                                <div key={i} className="leading-6">
                                    {highlighted}
                                </div>
                            );
                        })}
                    </code>
                </pre>
            </div>
        );
    }

    const lines = activeTab === 'install' ? installLines : monitorLines;

    return (
        <div className="relative">
            <button
                onClick={handleCopy}
                className="absolute top-3 right-3 p-2 hover:bg-white/10 rounded transition-colors z-10"
                title="Copy to clipboard"
            >
                {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                ) : (
                    <Copy className="w-4 h-4 text-gray-500" />
                )}
            </button>
            <div className="font-mono text-sm space-y-1">
                {lines.slice(0, displayedLines).map((line, i) => (
                    <div key={i} className="flex items-start gap-2">
                        {line.prompt && <span className="text-gray-500 select-none">$</span>}
                        {line.header ? (
                            <span className="text-gray-500">{line.text}</span>
                        ) : line.prompt ? (
                            <span className="text-gray-300">{line.text}</span>
                        ) : (
                            <span className="text-gray-400 pl-4">{line.text}</span>
                        )}
                    </div>
                ))}
                {displayedLines < lines.length && (
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500">$</span>
                        <span className="w-2 h-4 bg-volt-500 animate-pulse" />
                    </div>
                )}
            </div>
        </div>
    );
};

export const TerminalDemo = () => {
    const [activeTab, setActiveTab] = useState<Tab>('install');

    const tabs: { id: Tab; label: string }[] = [
        { id: 'install', label: 'Install' },
        { id: 'configure', label: 'Configure' },
        { id: 'monitor', label: 'Monitor' },
    ];

    return (
        <section className="py-32 lg:py-40 bg-obsidian-800">
            <div className="max-w-4xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-12">
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                        Get started in minutes.
                    </h2>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Sign up to get your helm install command. Define your SLO. Start saving.
                        No agents, no sidecars, no vendor lock-in.
                    </p>
                </div>

                {/* Terminal window */}
                <div className="rounded-xl overflow-hidden border border-white/10 bg-obsidian-900">
                    {/* Terminal header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02] border-b border-white/10">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500/80" />
                            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                            <div className="w-3 h-3 rounded-full bg-green-500/80" />
                        </div>
                        {/* Tabs */}
                        <div className="flex items-center gap-1">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                                        activeTab === tab.id
                                            ? 'bg-volt-500 text-obsidian-900'
                                            : 'text-gray-400 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <div className="w-[68px]" /> {/* Spacer for balance */}
                    </div>

                    {/* Terminal content */}
                    <div className="p-6 min-h-[300px]">
                        <TabContent activeTab={activeTab} />
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="mt-8 text-center">
                    <p className="text-gray-400 mb-4">
                        Ready to optimize your inference costs?
                    </p>
                    <a
                        href="/trial"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-volt-500 text-obsidian-900 rounded-lg font-semibold hover:bg-volt-400 transition-colors"
                    >
                        Start Free Trial
                    </a>
                </div>
            </div>

            {/* Section divider */}
            <div className="max-w-xl mx-auto mt-32">
                <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>
        </section>
    );
};
